import React, { useState, useEffect, useMemo } from 'react';
import { Button, Tag, Spin, Empty, Table, Row, Col, Tabs, message } from 'antd';
import {
  ArrowLeftOutlined, MailOutlined, PhoneOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { trainingService } from '@iso/services';

const getStatusTag = (status) => {
  const map = {
    compliant: { className: 'status-compliant', label: 'Competent' },
    expiring_soon: { className: 'status-expiring', label: 'Expiring Soon' },
    expired: { className: 'status-expired', label: 'Expired' },
    not_competent: { className: 'status-missing', label: 'Not Competent' },
  };
  const s = map[status] || { className: 'status-missing', label: status || 'Unknown' };
  return <Tag className={s.className}>{s.label}</Tag>;
};

const getTypeColor = (type) => {
  const map = { staff: '#0B2A1B', visitor: '#1890ff', contractor: '#fa8c16' };
  return map[type] || '#8c8c8c';
};

const getDaysClass = (days) => {
  if (days === null || days === undefined) return '';
  if (days <= 0) return 'days-expired';
  if (days <= 30) return 'days-critical';
  if (days <= 90) return 'days-warning';
  return 'days-ok';
};

const PersonDetail = ({ propertyId, person, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('records');

  const personId = person ? person.id : null;
  const sourceType = person ? (person.source_type || 'user') : null;

  useEffect(() => {
    if (!propertyId || !personId) return undefined;

    let alive = true;
    setLoading(true);

    (async () => {
      try {
        const res = await trainingService.getPersonDetail(propertyId, personId, sourceType);
        if (!alive) return;
        if (res && res.person) {
          setDetail(res.person);
        } else {
          message.error('Failed to load person detail');
        }
      } catch (e) {
        if (alive) {
          console.error('PersonDetail fetch error:', e);
          message.error('Failed to load person detail');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [propertyId, personId, sourceType]);

  // All hooks must run on every render — keep useMemo calls above the
  // loading / !detail early returns to satisfy rules-of-hooks.
  const trainingRecords = detail?.training_records || [];

  const recordColumns = useMemo(() => [
    {
      title: 'Training / Course',
      dataIndex: 'training_name',
      key: 'training_name',
      render: (t, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{t}</div>
          {r.category && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.category}</div>}
        </div>
      ),
    },
    {
      title: 'Completed Date',
      dataIndex: 'completed_date',
      key: 'completed_date',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Days Until Expiry',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      render: d => {
        if (d === null || d === undefined) return <span style={{ color: '#8c8c8c' }}>—</span>;
        return (
          <span className={getDaysClass(d)}>
            {d <= 0 ? `${Math.abs(d)} days overdue` : `${d} days`}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
  ], []);

  const expiringRecords = useMemo(
    () => trainingRecords.filter(r => r.status === 'expiring_soon' || r.status === 'expired' || r.status === 'not_competent'),
    [trainingRecords]
  );

  const expiringColumns = useMemo(() => [
    {
      title: 'Training / Course',
      dataIndex: 'training_name',
      key: 'training_name',
      render: (t, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{t}</div>
          {r.category && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.category}</div>}
        </div>
      ),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Days Until Expiry',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      render: d => {
        if (d === null || d === undefined) return <span style={{ color: '#8c8c8c' }}>—</span>;
        return (
          <span className={getDaysClass(d)}>
            {d <= 0 ? `${Math.abs(d)} days overdue` : `${d} days`}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
  ], []);

  const sessionColumns = useMemo(() => [
    {
      title: 'Training',
      dataIndex: 'training_name',
      key: 'training_name',
      render: t => <span style={{ fontWeight: 600 }}>{t}</span>,
    },
    {
      title: 'Date',
      dataIndex: 'session_date',
      key: 'session_date',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer',
      key: 'trainer',
      render: t => t || '—',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: t => t || '—',
    },
    {
      title: 'Delivery',
      dataIndex: 'delivery_method',
      key: 'delivery_method',
      render: t => t ? <Tag>{t.replace(/_/g, ' ')}</Tag> : '—',
    },
  ], []);

  if (loading) {
    return (
      <div className="tab-content">
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="tab-content">
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16, padding: 0, color: '#0B2A1B' }}>
          Back to Search
        </Button>
        <Empty description="Person not found" />
      </div>
    );
  }

  const { person: personInfo, compliance_summary, session_history } = detail;
  const totalRecords = (compliance_summary.compliant || 0) + (compliance_summary.expiring_soon || 0) +
    (compliance_summary.expired || 0) + (compliance_summary.not_competent || 0);

  const tabItems = [
    {
      key: 'records',
      label: 'Training Records',
      children: (
        <Table
          dataSource={trainingRecords}
          columns={recordColumns}
          rowKey="training_asset_id"
          pagination={false}
          scroll={{ x: 700 }}
        />
      ),
    },
    {
      key: 'expiring',
      label: `Expiring / Not Competent (${expiringRecords.length})`,
      children: expiringRecords.length === 0 ? (
        <Empty description="No expiring or not-competent training" style={{ marginTop: 40 }} />
      ) : (
        <Table
          dataSource={expiringRecords}
          columns={expiringColumns}
          rowKey="training_asset_id"
          pagination={false}
          scroll={{ x: 600 }}
        />
      ),
    },
    {
      key: 'history',
      label: 'Session History',
      children: session_history.length === 0 ? (
        <Empty description="No session history" style={{ marginTop: 40 }} />
      ) : (
        <Table
          dataSource={session_history}
          columns={sessionColumns}
          rowKey="session_id"
          pagination={false}
          scroll={{ x: 700 }}
        />
      ),
    },
  ];

  return (
    <div className="tab-content">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        style={{ marginBottom: 16, padding: 0, color: '#0B2A1B', fontWeight: 500 }}
      >
        Back to Search
      </Button>

      {/* Person Header */}
      <div className="person-detail-header">
        <div className="person-detail-avatar" style={{ background: getTypeColor(personInfo.person_type) }}>
          {(Array.from(personInfo.name || '?')[0] || '?').toUpperCase()}
        </div>
        <div className="person-detail-info">
          <div className="person-detail-name">
            {personInfo.name}
            <Tag color={getTypeColor(personInfo.person_type)} style={{ marginLeft: 10, textTransform: 'capitalize' }}>
              {personInfo.person_type}
            </Tag>
          </div>
          <div className="person-detail-contact">
            {personInfo.email && (
              <span><MailOutlined style={{ marginRight: 4 }} />{personInfo.email}</span>
            )}
            {personInfo.phone && (
              <span><PhoneOutlined style={{ marginRight: 4 }} />{personInfo.phone}</span>
            )}
          </div>
        </div>
      </div>

      {/* Compliance Summary */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <div className="person-stat-card">
            <span className="person-stat-count" style={{ color: '#0B2A1B' }}>{totalRecords}</span>
            <span className="person-stat-label">Total Records</span>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div className="person-stat-card snapshot-compliant">
            <span className="person-stat-count">{compliance_summary.compliant}</span>
            <span className="person-stat-label">Competent</span>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div className="person-stat-card snapshot-expiring">
            <span className="person-stat-count">{compliance_summary.expiring_soon}</span>
            <span className="person-stat-label">Expiring</span>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div className="person-stat-card snapshot-expired">
            <span className="person-stat-count">{(compliance_summary.expired || 0) + (compliance_summary.not_competent || 0)}</span>
            <span className="person-stat-label">Expired / Not Competent</span>
          </div>
        </Col>
      </Row>

      {/* Tabs */}
      <div className="person-detail-tabs">
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </div>
    </div>
  );
};

export default PersonDetail;
